import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { refreshToken as apiRefreshToken, getAccessTokenExpiration } from "@/api/auth";

const FALLBACK_DURATION_MS = 60 * 60 * 1000; // Fallback, falls keine Expiration im Storage liegt
const ACTIVITY_THROTTLE_MS = 30_000; // Backend-Refresh max. alle 30 s
const TICK_INTERVAL_MS = 1000;

// Routen, auf denen kein Auto-Logout-Tracking laufen soll
const PUBLIC_PATHS = ["/login", "/verify-2fa", "/change-password"];

interface SessionContextValue {
  /** Verbleibende Zeit in Sekunden bis zum automatischen Logout */
  remainingSeconds: number;
  /** Aktivität manuell signalisieren (z. B. nach erfolgreicher API-Aktion) */
  reportActivity: () => void;
  /** Aktiver Tracking-Status */
  isTracking: boolean;
}

const DEFAULT_SESSION: SessionContextValue = {
  remainingSeconds: FALLBACK_DURATION_MS / 1000,
  reportActivity: () => {},
  isTracking: false,
};

const SessionContext = createContext<SessionContextValue>(DEFAULT_SESSION);

function clearAuthStorage() {
  try {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("accessTokenExpiration");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("userIk");
    localStorage.removeItem("krankenhausName");
    localStorage.removeItem("pendingUsername");
  } catch {}
}

function hasToken(): boolean {
  try {
    return Boolean(localStorage.getItem("accessToken"));
  } catch {
    return false;
  }
}

/** Liest die aktuell gespeicherte Token-Expiration in ms-Timestamp. */
function readExpirationMs(): number | null {
  const d = getAccessTokenExpiration();
  return d ? d.getTime() : null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const lastActivityRef = useRef<number>(0);
  const refreshInFlightRef = useRef<boolean>(false);
  const isLoggingOutRef = useRef<boolean>(false);

  const computeInitialRemaining = (): number => {
    const expMs = readExpirationMs();
    if (expMs == null) return Math.floor(FALLBACK_DURATION_MS / 1000);
    return Math.max(0, Math.ceil((expMs - Date.now()) / 1000));
  };

  const [remainingSeconds, setRemainingSeconds] = useState<number>(computeInitialRemaining);

  const isPublicRoute = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p));
  const isTracking = !isPublicRoute && hasToken();

  const performAutoLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    clearAuthStorage();
    navigate("/login?reason=inactivity", { replace: true });
  }, [navigate]);

  // Zentrale Abmeldung bei einer 401-Antwort vom Backend (Token abgelaufen/ungültig).
  // Wird über das globale Event `whik:auth-expired` aus dem HTTP-Client ausgelöst.
  const performSessionExpiredLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    clearAuthStorage();
    navigate("/login?reason=session_expired", { replace: true });
  }, [navigate]);

  const reportActivity = useCallback(() => {
    if (!isTracking || isLoggingOutRef.current) return;

    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityRef.current = now;

    // Bei jeder (gedrosselten) Aktivität Token beim Backend refreshen,
    // damit die Session während der Arbeit nicht abläuft.
    if (refreshInFlightRef.current) return;

    refreshInFlightRef.current = true;
    apiRefreshToken()
      .then(() => {
        // Direkt nach erfolgreichem Refresh die Restzeit aktualisieren
        const newExp = readExpirationMs();
        if (newExp != null) {
          setRemainingSeconds(Math.max(0, Math.ceil((newExp - Date.now()) / 1000)));
        }
      })
      .catch(() => {
        // Fehler ignorieren — der Tick-Loop loggt bei Ablauf aus.
      })
      .finally(() => {
        refreshInFlightRef.current = false;
      });
  }, [isTracking]);

  // Beim Wechsel zu/von einer geschützten Route den Logout-Lock zurücksetzen
  useEffect(() => {
    if (isTracking) {
      isLoggingOutRef.current = false;
      const expMs = readExpirationMs();
      if (expMs != null) {
        setRemainingSeconds(Math.max(0, Math.ceil((expMs - Date.now()) / 1000)));
      }
    }
  }, [isTracking]);

  // Aktivität wird ausschließlich durch Backend-Kommunikation getriggert.
  // Der HTTP-Client feuert nach jedem Request ein CustomEvent.
  useEffect(() => {
    if (!isTracking) return;

    const handler = () => reportActivity();
    window.addEventListener("whik:api-activity", handler);
    return () => {
      window.removeEventListener("whik:api-activity", handler);
    };
  }, [isTracking, reportActivity]);

  // Globaler Listener für 401-Antworten: sofortige Abmeldung, unabhängig von der Route.
  // (Nicht an isTracking gebunden, damit auch ein abgelaufener Token auf einer
  //  nicht-getrackten Route zuverlässig zum Login-Redirect führt.)
  useEffect(() => {
    const handler = () => performSessionExpiredLogout();
    window.addEventListener("whik:auth-expired", handler);
    return () => {
      window.removeEventListener("whik:auth-expired", handler);
    };
  }, [performSessionExpiredLogout]);

  // Route-Wechsel zählt als Aktivität (löst typischerweise eh API-Calls aus)
  useEffect(() => {
    if (isTracking) reportActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Countdown-Tick: liest jede Sekunde die echte Token-Expiration aus dem Storage.
  useEffect(() => {
    if (!isTracking) return;

    const tick = () => {
      const expMs = readExpirationMs();
      if (expMs == null) {
        // Kein Token mehr im Storage → ausloggen
        performAutoLogout();
        return;
      }
      const remainingMs = expMs - Date.now();
      if (remainingMs <= 0) {
        setRemainingSeconds(0);
        performAutoLogout();
      } else {
        setRemainingSeconds(Math.ceil(remainingMs / 1000));
      }
    };

    tick();
    const interval = window.setInterval(tick, TICK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [isTracking, performAutoLogout]);

  return (
    <SessionContext.Provider value={{ remainingSeconds, reportActivity, isTracking }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}

/** Formatiert Sekunden als mm:ss */
export function formatRemaining(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
